<template>
  <div
    class="bg-white p-6 lg:p-8 w-full relative min-h-[650px] rounded-xl rounded-tl-none"
  >
    <button
      v-if="activeView === VIEW.UPSERT"
      class="btn btn-trans-black btn-black m-0 px-4 py-3"
      @click="routeToView"
    >
      {{ BUTTON_TEXT.BACK }}
    </button>
    <FormUpsertReport v-if="activeView === VIEW.UPSERT" />

    <div v-else-if="isAdmin" class="bg-white w-full">
      <div
        class="flex flex-col md:flex-row justify-between md:items-center gap-4 w-full pb-8 transition-all border-b border-truegray-300"
        :class="animating ? 'opacity-0' : 'transition-all'"
      >
        <h2 class="mb-0">Reports</h2>

        <ModalFilter
          :filters="modalFilters"
          :context="'reports'"
          :is-loading="isLoading"
          @on-submit="submitFilters"
        />
      </div>

      <div class="flex flex-col w-full">
        <FilterFormActiveFilters
          v-show="showFilterFormActiveFilters"
          @on-submit="submitFilters"
        />

        <div
          class="flex flex-col lg:flex-row justify-center"
          :class="{ 'mt-6': !showFilterFormActiveFilters }"
        >
          <!-- Reports Overview (LHS) -->
          <div
            class="w-full lg:w-1/3 flex-shrink-0 lg:pr-8 lg:border-r lg:border-truegray-300 mb-10 pb-10 border-b lg:border-b-0 lg:mb-0 lg:pb-0"
          >
            <h3 class="text-lg">Reports Overview</h3>
            <div>
              <FormulateForm v-model="graphForm">
                <FormulateInput
                  name="graphType"
                  type="select"
                  label="Compare reports:"
                  placeholder="Select graph type"
                  :options="GRAPH_TYPES"
                  :outer-class="['mb-4']"
                />
                <FormulateInput
                  v-show="showGraph('pie')"
                  name="graphPieCategory"
                  type="select"
                  label="Compare by category:"
                  placeholder="Select category to compare"
                  :options="graphPieCategoryOptions"
                />

                <FormulateInput
                  v-show="showGraph('bar')"
                  name="graphBarUnit"
                  type="select"
                  label="Compare by time scale:"
                  placeholder="Select time units/scale"
                  :options="graphBarUnitOptions"
                />
              </FormulateForm>
              <div class="mt-8">
                <template v-if="hasReports">
                  <div
                    v-if="showGraph('pie')"
                    class="flex flex-col items-center justify-center w-full"
                  >
                    <div class="w-full" :style="{ maxWidth: '225px' }">
                      <Doughnut
                        ref="cwChartReportsPie"
                        chart-id="'cwChartReportsPie'"
                        :chart-data="graphPieData"
                        :chart-options="graphConfigPie"
                        css-classes="relative z-10"
                        :width="80"
                        :height="80"
                      />
                    </div>
                    <div
                      class="flex flex-wrap gap-4 items-center justify-center px-4 mt-8"
                    >
                      <div
                        v-for="(label, index) in graphPieData.labels"
                        :key="index"
                        class="flex items-center"
                      >
                        <span
                          class="w-4 h-4 rounded-full mr-2"
                          :style="{
                            backgroundColor:
                              GRAPH_BACKGROUND_COLORS[
                                index % GRAPH_BACKGROUND_COLORS.length
                              ],
                          }"
                        />
                        <span class="text-xs text-truegray-500">
                          {{ label }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Bar
                    v-if="showGraph('bar')"
                    ref="cwChartReportsBar"
                    chart-id="cwChartReportsBar"
                    :chart-data="graphBarData"
                    :chart-options="graphConfigBar"
                  />
                </template>
                <template v-else>
                  <p class="italic">No report data...</p>
                </template>
              </div>
            </div>
          </div>
          <!-- Reports List (RHS) -->
          <div class="w-full lg:w-2/3 flex-shrink-0 lg:pl-8">
            <div class="flex flex-row justify-between items-center w-full mb-6">
              <h3 class="mb-0 text-lg">{{ reportsTitle }}</h3>
              <button
                class="btn btn-primary m-0 px-4 py-3"
                @click="routeToView"
              >
                {{ BUTTON_TEXT.ADD_REPORT }}
              </button>
            </div>

            <div v-if="!isLoading">
              <p v-if="!hasReports" class="italic">No reports found...</p>
              <div v-else class="w-full">
                <div class="flex flex-col space-y-4">
                  <CardReport
                    v-for="report in reports"
                    :key="report.id"
                    :report="report"
                  />
                </div>
                <!-- Pagination -->
                <div
                  v-if="
                    reportsPagination.previousPage || reportsPagination.nextPage
                  "
                  class="flex justify-between items-center h-10 mt-6"
                >
                  <span
                    :disabled="!reportsPagination.previousPage"
                    :class="[
                      !reportsPagination.previousPage
                        ? 'opacity-25 cursor-not-allowed'
                        : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
                    ]"
                    class="text-gray-500"
                    @click="paginateReports(reportsPagination.previousPage)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 17l-5-5m0 0l5-5m-5 5h12"
                      />
                    </svg>
                  </span>
                  <span class="text-center text-xs"
                    >Page {{ reportsPagination.currentPage }} of
                    {{ reportsPagination.lastPage }}</span
                  >
                  <span
                    :disabled="!reportsPagination.nextPage"
                    :class="[
                      !reportsPagination.nextPage
                        ? 'opacity-25 cursor-not-allowed'
                        : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
                    ]"
                    class="text-gray-500"
                    @click="paginateReports(reportsPagination.nextPage)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <div v-else class="w-full h-24 flex items-center justify-center">
              <lottie :options="loadingAnimationOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, ref, watch, nextTick } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { useRoute, useRouter } from 'vue-router/composables'
import * as loadingAnimation from '../../../shared/components/loading-dark.json'
import { Bar, Doughnut } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'
import CardReport from './components/CardReport.vue'
import FilterFormActiveFilters from './components/FilterFormActiveFilters.vue'
import FormUpsertReport from './FormUpsertReport.vue'
import ModalFilter from './components/modal/ModalFilter.vue'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { EVENT_KEYS } from '../../../js/util/constants.js'

export default defineComponent({
  name: 'TabReports',

  components: {
    Bar,
    Doughnut,
    CardReport,
    FilterFormActiveFilters,
    FormUpsertReport,
    ModalFilter,
  },

  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    ChartJS.register(
      ArcElement,
      BarElement,
      CategoryScale,
      Legend,
      LinearScale,
      Tooltip,

      {
        id: 'DoughnutCenterText',
        afterDraw: (chart) => {
          if (chart?.config?.options?.elements?.center) {
            const ctx = chart.ctx
            const centerConfig = chart.config.options.elements.center
            const titleConfig = centerConfig.title
            const subtitleConfig = centerConfig.subtitle

            ctx.save()
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'

            const title = titleConfig.text
            const subtitle = subtitleConfig.text
            const textX = Math.round(
                (chart.chartArea.left + chart.chartArea.right) / 2,
              ),
              textY = Math.round(
                (chart.chartArea.top + chart.chartArea.bottom) / 2,
              )

            // Configure title text
            ctx.font = titleConfig.font
            ctx.fillStyle = titleConfig.color
            ctx.fillText(title, textX, textY - 15)

            // Configure subtitle text
            ctx.font = subtitleConfig.font
            ctx.fillStyle = subtitleConfig.color
            ctx.fillText(subtitle, textX, textY + 15)
            ctx.restore()
          }
        },
      },
    )

    // LOADING/ANIMATIONS
    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)
    const animating = ref(false)

    // DATA
    const isAdmin = computed(() => {
      return store.getters['shared/getUserAccess']
    })

    // VIEWS/ROUTES/TABS
    const VIEW = {
      REPORTS: 'reports',
      UPSERT: 'upsert',
    }
    const ROUTES_UPSERT = ['ManagerAddReport', 'ManagerUpdateReport']

    const activeView = ref(VIEW.REPORTS)
    const BUTTON_TEXT = {
      BACK: '< Back to Reports',
      ADD_REPORT: '+ Add Report',
    }

    const changeView = (newView = null) => {
      const toggledView =
        activeView.value === VIEW.REPORTS ? VIEW.UPSERT : VIEW.REPORTS
      newView = newView || toggledView
      activeView.value = newView
    }

    const routeToView = () => {
      const name =
        activeView.value === VIEW.REPORTS ? ROUTES_UPSERT[0] : 'ManagerReports'
      if (route.name !== name) {
        router.push({ name })
      }
    }

    const watchRoute = () => {
      const value = ROUTES_UPSERT.includes(route.name)
        ? VIEW.UPSERT
        : VIEW.REPORTS
      changeView(value)

      if (value === VIEW.REPORTS) {
        initGraphForm(true)
      }
    }

    watch(route, () => watchRoute())

    // REPORTS
    const reports = computed(() => store.getters['connections/getReports'])
    const reportsTitle = computed(() => {
      // @TODO 'hasSubmitted' ?
      return selectedFiltersCount.value ? 'Matching Reports' : 'All Reports'
    })
    const totalReports = computed(() => reportsPagination.value?.total || 0)
    const hasReports = computed(() => !!totalReports.value)

    const reportsPagination = ref({})

    const paginateReports = (page) => {
      getReports({ page })
    }

    const getReports = async ({
      page = 1,
      filters = store.getters['connections/getFilterFormPayload'],
      statsConfig = graphPayload.value,
    } = {}) => {
      if (route.meta?.context === 'reports') {
        isLoading.value = true
        const paginationObj = await store.dispatch(
          'connections/dispatchUpdateReports',
          {
            page,
            filters,
            statsConfig,
          },
        )
        animating.value = false
        isLoading.value = false
        if (paginationObj) {
          // setReportsPagination(paginationObj)
          reportsPagination.value = paginationObj
        }
      }
    }

    // CHARTS/GRAPHS
    const GRAPH_TYPES = [
      { label: 'By category', value: 'pie' },
      { label: 'Over time', value: 'bar' },
    ]

    const GRAPH_BACKGROUND_COLORS = [
      '#1E90FF',
      '#4169E1',
      '#6A5ACD',
      '#9370DB',
      '#BA55D3',
      '#EE82EE',
      '#FF69B4',
      '#FF1493',
      '#FF4500',
      '#FF0000',
    ]

    // PIE GRAPH
    const graphConfigPie = computed(() => {
      return {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '68%',
        plugins: {
          legend: {
            display: false,
            // align: 'start',
            // labels: {
            //   pointStyle: 'circle',
            //   usePointStyle: true,
            //   pointStyleWidth: 18,
            // },
          },
        },

        elements: {
          center: {
            title: {
              text: totalReports.value,
              color: '#6f36bc',
              font: '600 28px Nunito',
            },
            subtitle: {
              text: 'Total',
              color: '#000000',
              font: '300 16px Nunito',
            },
          },
        },
      }
    })

    const graphPieCategoryOptions = computed(
      () =>
        store.getters[
          'shared/getTagsCategoriesByIdMappedForVueFormulateReports'
        ],
    )

    const graphPieData = computed(() => {
      const stats = store.getters['connections/getReportsStats']

      // Array of tag text/labels under selected category
      const labels = Object.keys(stats)?.map((tagId) =>
        store.getters['shared/getTagTextByTagId'](tagId),
      )

      // Array of report counts for each tag id under selected category
      const data = Object.values(stats)?.map((stats) => stats.count)

      return {
        labels,
        datasets: [
          {
            backgroundColor: GRAPH_BACKGROUND_COLORS,
            data,
          },
        ],
      }
    })

    // BAR GRAPH
    const graphConfigBar = computed(() => {
      return {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: false,
          },
        },

        scales: {
          x: {
            title: {
              display: isMonthlySelected.value,
              text: selectedYear.value,
            },
          },

          y: {
            title: {
              display: true,
              text: 'Reports',
            },

            beginAtZero: true,
            ticks: {
              stepSize: 1,
              precision: 0,
            },
          },
        },
      }
    })

    const graphBarUnitOptions = [
      { label: 'Monthly', value: 'monthly' },
      { label: 'Yearly', value: 'yearly' },
    ]

    const graphBarData = computed(() => {
      const stats = store.getters['connections/getReportsStats']

      // Array of time units
      const labels = Object.keys(stats)

      // Array of report counts for each tag id under selected category
      const data = Object.values(stats)?.map((stats) => stats.count)

      return {
        labels,
        datasets: [
          {
            backgroundColor: GRAPH_BACKGROUND_COLORS,
            data,
          },
        ],
      }
    })

    const isMonthlySelected = computed(
      () => graphForm.value?.graphBarUnit === 'monthly',
    )
    const selectedYear = computed(
      () => store.getters['connections/getFilterFormSelectedYear'],
    )

    const graphForm = ref({
      graphType: null,
      graphPieCategory: null,
      graphBarUnit: null,
    })

    const graphPayload = computed(() => {
      const data = showGraph('pie')
        ? graphForm.value.graphPieCategory
        : graphForm.value.graphBarUnit

      return {
        type: graphForm.value.graphType,
        data,
      }
    })

    const showGraph = (type) => {
      return graphForm.value.graphType === type
    }

    // FILTERS
    const modalFilters = computed(() => {
      const filters = [
        'dates',
        'industries',
        'subjects',
        'engagementTypes',
        'years',
        'techSkills',
        'softSkills',
      ]

      if (showGraph('pie')) {
        filters.push('dateYear')
        filters.push('dateRange')
      } else {
        if (isMonthlySelected.value) {
          filters.push('dateYear')
        } else {
          filters.push('dateRange')
        }
      }

      return filters
    })

    const selectedFiltersCount = computed(
      () => store.getters['connections/getSelectedFiltersCount'],
    )

    const showFilterFormActiveFilters = computed(
      () => selectedFiltersCount.value,
    )

    const submitFilters = () => {
      getReports()
    }

    const initGraphForm = async (resetGraphType = false) => {
      if (resetGraphType) {
        graphForm.value.graphType = GRAPH_TYPES[0].value
      }

      graphForm.value.graphPieCategory = null
      graphForm.value.graphBarUnit = null
      await nextTick()
      // Default to 'Subjects' (key 4)
      graphForm.value.graphPieCategory = Object.keys(
        graphPieCategoryOptions.value,
      ).find((el) => el === '4')
      // Default to 'Monthly'
      graphForm.value.graphBarUnit = graphBarUnitOptions[0].value
    }

    watch(
      () => graphForm.value.graphType,
      () => initGraphForm(),
    )

    watch(
      () => graphForm.value.graphPieCategory,
      async (newVal) => {
        if (newVal && showGraph('pie')) {
          await nextTick()
          getReports()
        }
      },
    )

    watch(
      () => graphForm.value.graphBarUnit,
      async (newVal) => {
        if (newVal && showGraph('bar')) {
          await nextTick()
          getReports()
        }
      },
    )

    onMounted(() => {
      watchRoute()

      EventBus.$on(EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION_REPORTS, () =>
        getReports(),
      )
    })

    return {
      // DATA
      isAdmin,
      loadingAnimationOptions,
      isLoading,
      animating,

      // VIEWS
      VIEW,
      BUTTON_TEXT,
      activeView,
      routeToView,
      changeView,

      // REPORTS
      reports,
      reportsTitle,
      hasReports,
      reportsPagination,
      paginateReports,
      getReports,

      // CHARTS/GRAPHS
      graphConfigPie,
      graphPieCategoryOptions,
      graphPieData,

      graphConfigBar,
      graphBarUnitOptions,
      graphBarData,

      GRAPH_TYPES,
      GRAPH_BACKGROUND_COLORS,
      graphForm,
      showGraph,

      // FILTERS
      modalFilters,
      showFilterFormActiveFilters,
      submitFilters,
    }
  },
})
</script>
